import {
  WALK_THRESHOLD_KM,
  WALK_SPEED_KMH,
  DRIVE_SPEED_KMH,
  MAX_REASONABLE_DRIVE_KM,
} from "@poi-planner/shared";

export interface Coords {
  lat: number;
  lng: number;
}

export interface TravelEstimate {
  distanceKm: number;
  distanceMiles: number;
  travelMinutes: number;
  mode: "walk" | "drive";
  icon: string;
}

const EARTH_RADIUS_KM = 6371;
const KM_TO_MILES = 0.621371;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function haversineDistanceKm(a: Coords, b: Coords): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

export function estimateTravel(a: Coords, b: Coords): TravelEstimate | null {
  const distanceKm = haversineDistanceKm(a, b);

  // Return null for unreasonably long distances (would need a flight/train, not driving)
  if (distanceKm > MAX_REASONABLE_DRIVE_KM) {
    return null;
  }

  const distanceMiles = distanceKm * KM_TO_MILES;
  const isWalk = distanceKm < WALK_THRESHOLD_KM;
  const speed = isWalk ? WALK_SPEED_KMH : DRIVE_SPEED_KMH;
  const travelMinutes = Math.max(1, Math.ceil((distanceKm / speed) * 60));
  return {
    distanceKm,
    distanceMiles,
    travelMinutes,
    mode: isWalk ? "walk" : "drive",
    icon: isWalk ? "directions_walk" : "directions_car",
  };
}

export function formatTravelEstimate(est: TravelEstimate): string {
  const miles =
    est.distanceMiles < 1
      ? `${est.distanceMiles.toFixed(1)} mi`
      : `${Math.round(est.distanceMiles)} mi`;
  return `${est.travelMinutes} min (${miles})`;
}
